import React from 'react';
import PropTypes from 'prop-types';
import LoadingState from 'components/ShippingFlow/components/LoadingState';

const DeliveryServiceSelector = ({
  legIndex,
  deliveryDate,
  shipDate,
  serviceOptions = [],
  selectedService,
  loading = false,
  error = null,
  onServiceSelect,
  formatPrice,
  className = '',
  // Style overrides
  labelClassName = '',
  serviceCardClassName = () => '',
  radioClassName = '',
  serviceTitleClassName = '',
  serviceDateClassName = '',
  priceClassName = ''
}) => {
  const defaultLabelClass = 'text-gray-600';
  const defaultServiceCardClass = (isSelected) => `
    w-full p-4 border rounded-lg text-left transition-all duration-200
    ${isSelected 
      ? 'border-blue-500 bg-blue-50' 
      : 'border-gray-200 hover:border-gray-300 bg-white'
    }
  `;
  const defaultRadioClass = 'mr-3 w-4 h-4 text-blue-600';
  const defaultServiceTitleClass = 'font-medium text-gray-900';
  const defaultServiceDateClass = 'text-sm text-gray-600';
  const defaultPriceClass = 'font-semibold text-gray-900';

  // Use provided classes or fall back to defaults
  const finalLabelClass = labelClassName || defaultLabelClass;
  const finalServiceCardClass = serviceCardClassName || defaultServiceCardClass;
  const finalRadioClass = radioClassName || defaultRadioClass;
  const finalServiceTitleClass = serviceTitleClassName || defaultServiceTitleClass;
  const finalServiceDateClass = serviceDateClassName || defaultServiceDateClass;
  const finalPriceClass = priceClassName || defaultPriceClass;

  // Sort services by cost (lowest to highest)
  const sortedServiceOptions = React.useMemo(() => {
    return [...serviceOptions].sort((a, b) => {
      const priceA = a.combined_adjusted_price_cents || a.price_cents || 0;
      const priceB = b.combined_adjusted_price_cents || b.price_cents || 0;
      return priceB - priceA;
    });
  }, [serviceOptions]);
  
  return (
    <div data-testid={`leg${legIndex}-services`} className={`space-y-3 ${className}`}>
      {!deliveryDate && !shipDate && (
        <div className={finalLabelClass}>
          Select a product and delivery date to see available services
        </div>
      )}
      {loading && <LoadingState message="Loading services..." className="border-none !bg-slate-200" />}

      {error && (
        <div className="text-red-500 text-center">
          Error loading services.<br />Please check your selections and try again.
        </div>
      )}
      
      {!loading && sortedServiceOptions.map((service) => (
        <button
          key={`${service.id}-${service.price_cents}`}
          onClick={() => onServiceSelect?.(service)}
          className={finalServiceCardClass(selectedService?.display_name === service.display_name)}
        >
          <div className="flex items-center">
            <div className="flex items-center flex-1">
              <input
                type="radio"
                checked={selectedService?.display_name === service.display_name}
                onChange={() => {}} // Handled by button click
                className={finalRadioClass}
                readOnly
              />
              <div className="flex-1">
                <div className={finalServiceTitleClass}>
                  {('delivery_guarantee' in service) && `Ship on ${service.ship_date}`}
                  {!('delivery_guarantee' in service) && `${service.arrival_date}`}
                </div>
                <div className={finalServiceDateClass}>
                  {service.display_name}
                </div>
              </div>
            </div>
            <div className={finalPriceClass}>
              {formatPrice(service.combined_adjusted_price_cents || service.price_cents)}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

DeliveryServiceSelector.propTypes = {
  legIndex: PropTypes.number.isRequired,
  deliveryDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  shipDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  serviceOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      arrival_date: PropTypes.string.isRequired,
      display_name: PropTypes.string.isRequired,
      combined_adjusted_price_cents: PropTypes.number.isRequired,
    })
  ),
  selectedService: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    display_name: PropTypes.string.isRequired,
  }),
  loading: PropTypes.bool,
  error: PropTypes.string,
  onServiceSelect: PropTypes.func.isRequired,
  formatPrice: PropTypes.func.isRequired,
  className: PropTypes.string,
  // Style prop types
  labelClassName: PropTypes.string,
  serviceCardClassName: PropTypes.func,
  radioClassName: PropTypes.string,
  serviceTitleClassName: PropTypes.string,
  serviceDateClassName: PropTypes.string,
  priceClassName: PropTypes.string
};

export default DeliveryServiceSelector;